import React from 'react';

import Button, { LinkButton } from '@atlaskit/button/new';

import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import { isFedrampModerate, isIsolatedCloud } from '@atlaskit/atlassian-context';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

type Props = {
	testId?: string;
	loading: boolean;
	globalComponentsEnabled: boolean;
	oldOnAddComponent: () => void;
	projectKey: string;
	verbose?: boolean;
};

const COMPASS_CREATE_COMPONENT_URL = (projectKey: string) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const projectLink = encodeURI(`${window.location.origin}/browse/${projectKey}`);
	return `/compass/home?showCreateComponentModal=true&projectLink=${projectLink}`;
};

export default function CreateComponentButton({
	testId,
	loading,
	globalComponentsEnabled = false,
	oldOnAddComponent,
	projectKey,
	verbose = false,
}: Props) {
	const { formatMessage } = useIntl();

	if (
		!isFedrampModerate() &&
		!(isIsolatedCloud() && fg('jira_fe_hide_components_list')) &&
		globalComponentsEnabled
	) {
		return (
			<LinkButton
				testId={testId}
				href={COMPASS_CREATE_COMPONENT_URL(projectKey)}
				target="_blank"
				rel="noopener noreferrer"
				appearance="primary"
				onClick={(_, analyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'createCompassComponent');
				}}
				iconAfter={ArrowUpRightIcon}
			>
				{formatMessage(verbose ? messages.addCompassComponent : messages.addComponent)}
			</LinkButton>
		);
	}

	return (
		<JSErrorBoundary
			fallback="unmount"
			packageName="jiraProjectDirectoriesComponents"
			teamName="compass-lodestone"
			id="createComponentButton"
			sendToPrivacyUnsafeSplunk
		>
			<Button isLoading={loading} testId={testId} appearance="primary" onClick={oldOnAddComponent}>
				{formatMessage(messages.addComponent)}
			</Button>
		</JSErrorBoundary>
	);
}
